import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';
import { Package, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useInputValidation } from '@/hooks/useInputValidation';
import chartFlowLogo from '@/assets/chart-flow-logo.png';

export const LoginForm = () => {
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { validateEmail, validatePassword, sanitizeInput } = useInputValidation();
  
  // États pour les formulaires
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', confirmPassword: '', fullName: '' });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous validation errors
    setValidationErrors({});
    
    // Validate inputs
    const emailValidation = validateEmail(loginData.email);
    const errors: Record<string, string> = {};
    
    if (!emailValidation.isValid) {
      errors.email = emailValidation.error || 'Email invalide';
    }
    
    if (!loginData.password) {
      errors.password = 'Mot de passe requis';
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setIsLoading(true);

    try {
      // Sanitize inputs
      const sanitizedEmail = sanitizeInput(loginData.email.trim().toLowerCase());
      const result = await login(sanitizedEmail, loginData.password);
      
      if (!result.success) {
        toast({
          title: "Erreur de connexion",
          description: result.error || "Email ou mot de passe incorrect",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la connexion",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous validation errors
    setValidationErrors({});
    
    // Validate inputs
    const emailValidation = validateEmail(registerData.email);
    const passwordValidation = validatePassword(registerData.password);
    const errors: Record<string, string> = {};
    
    if (!emailValidation.isValid) {
      errors.email = emailValidation.error || 'Email invalide';
    }
    
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.error || 'Mot de passe invalide';
    }
    
    if (registerData.password !== registerData.confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    if (registerData.fullName.trim().length < 2) {
      errors.fullName = 'Le nom doit contenir au moins 2 caractères';
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      // Sanitize inputs
      const sanitizedEmail = sanitizeInput(registerData.email.trim().toLowerCase());
      const sanitizedFullName = sanitizeInput(registerData.fullName.trim());
      
      const result = await register(sanitizedEmail, registerData.password, sanitizedFullName);
      if (!result.success) {
        toast({
          title: "Erreur d'inscription",
          description: result.error || "Une erreur est survenue",
          variant: "destructive"
        });
      } else {
        toast({
          title: "✅ Inscription réussie !",
          description: "📧 Un email de confirmation a été envoyé. Veuillez vérifier votre boîte de réception pour activer votre compte.",
          variant: "default",
          duration: 8000
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'inscription",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-subtle p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 rounded-xl flex items-center justify-center mb-4 shadow-elegant bg-white">
            <img src={chartFlowLogo} alt="Chart-Flow Logo" className="w-12 h-12" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Chart-Flow</h1>
          <p className="text-muted-foreground mt-2">Gestion d'inventaire moderne</p>
        </div>

        <Card className="shadow-card border-0">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">Bienvenue</CardTitle>
            <CardDescription className="text-center">
              Connectez-vous ou créez un compte pour continuer
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Connexion</TabsTrigger>
                <TabsTrigger value="register">Inscription</TabsTrigger>
              </TabsList>
              
              {/* Formulaire de connexion */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="votre@email.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                      required
                      className={`transition-all duration-200 focus:shadow-md ${validationErrors.email ? 'border-destructive' : ''}`}
                    />
                    {validationErrors.email && (
                      <div className="flex items-center gap-1 text-sm text-destructive mt-1">
                        <AlertCircle className="w-4 h-4" />
                        {validationErrors.email}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Mot de passe</Label>
                    <div className="relative">
                        <Input
                          id="login-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                          required
                          className={`pr-10 transition-all duration-200 focus:shadow-md ${validationErrors.password ? 'border-destructive' : ''}`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                      {validationErrors.password && (
                        <div className="flex items-center gap-1 text-sm text-destructive mt-1">
                          <AlertCircle className="w-4 h-4" />
                          {validationErrors.password}
                        </div>
                      )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-primary hover:shadow-elegant transition-all duration-300"
                    disabled={isLoading}
                  >
                    {isLoading ? "Connexion..." : "Se connecter"}
                  </Button>
                  <p className="text-center text-sm text-muted-foreground cursor-pointer hover:text-primary transition-colors">
                    Mot de passe oublié ?
                  </p>
                </form>
              </TabsContent>

              {/* Formulaire d'inscription */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-fullname">Nom complet</Label>
                    <Input
                      id="register-fullname"
                      type="text"
                      placeholder="Votre nom complet"
                      value={registerData.fullName}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, fullName: e.target.value }))}
                      className={`transition-all duration-200 focus:shadow-md ${validationErrors.fullName ? 'border-destructive' : ''}`}
                    />
                    {validationErrors.fullName && (
                      <div className="flex items-center gap-1 text-sm text-destructive mt-1">
                        <AlertCircle className="w-4 h-4" />
                        {validationErrors.fullName}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="votre@email.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, email: e.target.value }))}
                      required
                      className={`transition-all duration-200 focus:shadow-md ${validationErrors.email ? 'border-destructive' : ''}`}
                    />
                    {validationErrors.email && (
                      <div className="flex items-center gap-1 text-sm text-destructive mt-1">
                        <AlertCircle className="w-4 h-4" />
                        {validationErrors.email}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Mot de passe</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.password}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, password: e.target.value }))}
                      required
                      className={`transition-all duration-200 focus:shadow-md ${validationErrors.password ? 'border-destructive' : ''}`}
                    />
                    {validationErrors.password && (
                      <div className="flex items-center gap-1 text-sm text-destructive mt-1">
                        <AlertCircle className="w-4 h-4" />
                        {validationErrors.password}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                      className={`transition-all duration-200 focus:shadow-md ${validationErrors.confirmPassword ? 'border-destructive' : ''}`}
                    />
                    {validationErrors.confirmPassword && (
                      <div className="flex items-center gap-1 text-sm text-destructive mt-1">
                        <AlertCircle className="w-4 h-4" />
                        {validationErrors.confirmPassword}
                      </div>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-primary hover:shadow-elegant transition-all duration-300"
                    disabled={isLoading}
                  >
                    {isLoading ? "Création..." : "Créer un compte"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

          </CardContent>
        </Card>
      </div>
    </div>
  );
};